import { defineComponent as _defineComponent } from 'vue'
import { ref, computed, watch, provide, onMounted, onUnmounted } from 'vue'
import i18n from '../../../VueI18n'
import { useRegisters } from '../../../@web/modules/@shared/composables/Registers.composable'
import { useMessages } from '../../../composables/useMessages'

import Button from '../../../components/Button/Button.vue'
import CrudForm from '../../../components/CrudForm/CrudForm.vue'
import CrudTable from '../../../components/CrudTable/CrudTable.vue'
import Dropdown from '../../../components/Dropdown/Dropdown.vue'
import GridPagination from '../../../components/GridPagination/GridPagination.vue'
import RadioButtons from '../../../components/RadioButtons/RadioButtons.vue'
import Tag from '../../../components/Tag/Tag.vue'
import TagWithButton from '../../../components/TagWithButton/TagWithButton.vue'

import CardPicker from '../../../components/CardPicker/CardPicker.vue'
import DropdownCheckbox from '../../../components/DropdownCheckbox/DropdownCheckbox.vue'
import DropdownCheckboxFk from '../../../components/DropdownCheckboxFk/DropdownCheckboxFk.vue'
import DropdownFk from '../../../components/DropdownFk/DropdownFk.vue'
import InputSearch from '../../../components/InputSearch/InputSearch.vue'
import InputText from '../../../components/InputText/InputText.vue'
import KebabMenu from '../../../components/KebabMenu/KebabMenu.vue'
import Loading from '../../../components/Loading/Loading.vue'

import ModelPreviewTemp from '../../../components/ModelPreviewTemp/ModelPreviewTemp.vue'
import ProcessamentoAvancado from '../ProcessamentoAvancado.vue'

import { getAllEstratoInventarioCubagem } from '../../../services/requests/EstratoInventarioCubagemRequests'
import { getAllProdutos } from '../../../services/requests/ProdutoRequest'
import {
	ajustaModeloHipsometrico,
	createEstratoInventario,
	deleteEstratoInventarioFlorestalByIdList,
	getAllEstratosInventarioFlorestalPagination,
	getAllHipsometricoWithGraphicByEiflId,
	processaInventario,
	saveChosenHipsometricoModel,
	updateEstratoInventarioFlorestalAsync,
	classificaComoInventarioOficial,
} from '../../../services/requests/EstratoInventarioFlorestalRequests'
import { processarQualidadeParaInventarioTradicional } from '../../../services/requests/EstratoInventarioDeQualidadeRequests'
import { getDropdownMedicaoProcessamento } from '../../../services/requests/MedicoesInventarioRequests'
import { getDropdownEstratosFlorestais } from '../../../services/requests/EstratoFlorestalRequests'
import { getInstalacoesSingleTrees } from '../../../services/requests/OrdemDeServicoRequests'

import { exportarCsv } from '../../../utils/csvHellpers'
import OfficialProcessingIcon from '../../../components/OfficialProcessingIcon/OfficialProcessingIcon.vue'
import ExclusionAlert from '../../../components/ExclusionAlert/ExclusionAlert.vue'
import DropdownCheckboxFKMedicao from '../../../components/DropdownCheckboxFkMedicao/DropdownCheckboxFKMedicao.vue'

import { ProcessingTypeEnum } from '../../../@core/processing/domain/enums/ProcessingType.enum'
import { ProcessingVariableEnum } from '../../../@core/processing/domain/enums/ProcessingVariable.enum'
import { ProcessingResolutionEnum } from '../../../@core/processing/domain/enums/ProcessingResolution.enum'
import { ForestInventoryStratum } from '../../../@core/processing/domain/entities/ForestInventoryStratum'
import { useSmartProcessingStore } from '../../../@web/modules/processing/stores/useSmartProcessingStore'
import { useRouter } from 'vue-router/composables'
import { ForestInventoryStratumRepository } from '../../../@core/processing/infra/repositories/ForestInventoryStratum.repository'
import { registryApi } from '../../../services/clients/AxiosClients'

// ── Messages composable ──

export default /*#__PURE__*/_defineComponent({
  __name: 'InventarioFlorestal',
  setup(__props) {

const { addMessage } = useMessages()

// ── Template ref ──
const paginationRef = ref<InstanceType<typeof GridPagination> | null>(null)

// ── Registers composable (replaces ControllingPaginationMixins + RegistersMixins) ──
const {
	quantityPerPage,
	setStartEnd,
	setQuantityPerPage,
	disableSaveButton,
	isEdit,
	modelList,
	orderTable,
	showForm,
	titleForm,
	textFilter,
	sortBy,
	sortType,
	isDeleteButtonDisabled,
	modelsIdWithCheckedToDelete,
	modelsToBeShowedAndTotal,
	cleanModel,
} = useRegisters({
	onTextFilterChange: () => {
		paginationRef.value?.resetToFirstPageInputHandle()
	},
})

const router = useRouter()

// ── Data (refs) ──
const tipoProcessamento = ref<string>(ProcessingTypeEnum.TRADITIONAL)
const medicoesOriginal = ref<any[]>([])
const filtrarMedicaoBruta = ref(false)
const variaveis = ref<string[]>([])
const totalRecords = ref(0)
const exportando = ref(false)
const showExclusionAlert = ref(false)
const descricao = ref('')
const modeloSalvo = ref<any>(undefined)
const escolhendoModelo = ref(false)
const processando = ref(false)
// Guarda de re-entrada do toggle de inventario oficial: sem ela, cliques repetidos na
// estrela disparam requisicoes concorrentes que se sobrescrevem.
const classificandoOficial = ref(false)
const descriptionChanged = ref(false)
const headersSelecionados = ref([
	'estrato',
	'eifl_tx_status',
	'medicao',
	'eifl_ds_descricao',
	'eifl_cd_codigo',
	'nomeModelo',
	'eifl_dt_processamento',
	'inventarioOficial',
])
const ordensServico = ref<any[]>([])
const modeloEscolhido = ref('')
const index = ref(0)
const modelos = ref<any[]>([])
const avancado = ref(true)
const nivelEstrato = ref('')
const produtosSelecionados = ref<string[]>([])
const inventarioCubagens = ref<any[]>([])
const produtos = ref<any[]>([])
const intention = ref<any>({})
const planejamentoModelo = ref(false)
const mostrarLoadingProcessando = ref(false)
const mensagemProcessando = ref(i18n.t('realizeOProcessamento') as string)

// Smart processing refs
const smartProcessingVariables = ref<string[]>([])
const smartResolution = ref<string>(ProcessingResolutionEnum.MEDIUM)

const model = ref<Record<string, any>>({
	id: '',
	eifl_ds_descricao: '',
	fk_mdco_medicao: '',
	fk_orsv_ordem_servico: '',
	fk_eicb_estrato_invcbg: '',
	eifl_is_subsequente: false,
	eifl_md_nivel_significancia: 0.05,
	eifl_md_altura_toco: 0.1,
	// Declarada aqui porque o Vue 2 nao torna reativa uma chave adicionada depois: sem
	// isso o dropdown de metodologia (singletrees) nao refletia a selecao do usuario.
	// O valor '' NAO pode ir para a API — ver metodologiaNumeroFustesParaEnvio().
	eifl_nm_metodologia_nfustes: '',
	inventarioOficial: true,
	novoInventario: false,
})

const tableConfiguration = ref({
	statusTable: i18n.t('loading') + '...',
	prefix: 'OS',
	subtitleCardPropertie: 'eifl_ds_descricao',
	titleCardPropertie: 'eifl_ds_descricao',
})

const tableHeaders = ref([
	{ key: 'eifl_cd_codigo', value: i18n.t('code'), isActive: true },
	{ key: 'eifl_dt_processamento', value: i18n.t('processingDate'), isActive: true },
	{ key: 'eifl_ds_descricao', value: i18n.t('description'), isActive: true },
	{ key: 'estrato', value: i18n.t('forestStratification'), isActive: true },
	{ key: 'ordemDeServico', value: i18n.t('serviceOrder'), isActive: false },
	{ key: 'medicao', value: i18n.t('measurement'), isActive: true },
	{ key: 'eifl_tx_status', value: i18n.t('status'), isActive: true },
	{ key: 'nomeModelo', value: i18n.t('model'), isActive: true },
	{ key: 'inventarioOficial', value: i18n.t('registration_status'), isActive: false },
])

const estratosFlorestais = ref<any[]>([])
const executoresSelecionados = ref<any[]>([])

// ── Computed ──
const medicoesComputadas = computed(() => {
	let medicoesParaProcessar = medicoesOriginal.value
	if (filtrarMedicaoBruta.value) {
		medicoesParaProcessar = medicoesParaProcessar.filter(
			(medicao: any) => medicao.medicaoProcessada === false,
		)
	}
	if (isSmart.value) {
		medicoesParaProcessar = medicoesParaProcessar.filter(
			(medicao: any) => medicao.possuiImagensParaProcessamento === true,
		)
	}
	return medicoesParaProcessar.map((medicao: any) => ({
		[i18n.t('measurement') as string]: `${medicao.codigoMedicao} - ${medicao.descricaoMedicao}`,
		[i18n.t('isMeasurementProcessed') as string]: medicao.medicaoProcessada
			? i18n.t('yes')
			: i18n.t('no'),
		[i18n.t('serviceOrder') as string]:
			`${medicao.codigoOrdemServico} - ${medicao.descricaoOrdemServico}`,
		[i18n.t('farm') as string]: `${medicao.codigoFazenda} - ${medicao.nomeFazenda}`,
		id: medicao.id,
	}))
})

const isSingleTrees = computed(() => tipoProcessamento.value === ProcessingTypeEnum.SINGLE_TREES)
const isSmart = computed(() => tipoProcessamento.value === ProcessingTypeEnum.SMART)
const hasSmartMeasurements = computed(() =>
	medicoesOriginal.value.some((m: any) => m.possuiImagensParaProcessamento === true),
)

/**
 * O processamento Smart e contratado por modulo. Mesma convencao do resto do sistema
 * (ver os guards de router.js e o gate de IA_SELECTED_MODELS em saveModel): a lista de
 * modulos do cliente fica na chave 'user-modules' do localStorage.
 * O fallback '[]' evita estourar quando a chave nao existe -- o restante do projeto faz
 * JSON.parse sem fallback e quebra nesse caso.
 */
const possuiModuloProcessamentoSmart = computed(() => {
	try {
		const userModules = JSON.parse(localStorage.getItem('user-modules') || '[]')
		return Array.isArray(userModules) && userModules.includes('PROCESSAMENTO_SMART')
	} catch {
		return false
	}
})

const smartIndisponivel = computed(
	() => !hasSmartMeasurements.value || !possuiModuloProcessamentoSmart.value,
)

/**
 * Opcao Smart do radio de tipo de processamento. Fica num computed porque os dois
 * formularios da tela exibem o mesmo radio em ordens diferentes -- sem isto a regra da
 * badge ficaria duplicada nos dois templates.
 * A badge so aparece quando falta o modulo: medicao sem imagens tambem desabilita a opcao,
 * mas ai nao e caso de upgrade e sim de dado ausente.
 */
const opcaoSmart = computed(() => ({
	label: i18n.t('smart'),
	value: ProcessingTypeEnum.SMART,
	disabled: smartIndisponivel.value,
	badge: possuiModuloProcessamentoSmart.value ? undefined : i18n.t('upgrade'),
	badgeTooltip: possuiModuloProcessamentoSmart.value
		? undefined
		: i18n.t('avisoUpgradeProcessamentoSmart'),
}))

const agregadoras = computed<Record<string, string>>(() => ({
	tlho_cd_codigo: 'talhao',
	eiflid: 'estrato',
	ptio_dt_plantio: 'idade',
	mtge_cd_codigo: 'matgen',
	prlaid: 'parcela',
}))

const edit = computed(() => isEdit.value)

const cubagens = computed(() => {
	let cubagensFiltradas = inventarioCubagens.value
	if (isSmart.value) {
		cubagensFiltradas = cubagensFiltradas.filter(
			(cubagem: any) => cubagem.eicb_tx_tipo_modelo === 'Volumetrico',
		)
	}
	return cubagensFiltradas.map((cubagem: any) => {
		return Object.assign(cubagem, {
			[i18n.t('modelName') as string]: cubagem.nomeModelo,
			[i18n.t('code') as string]: cubagem.eicb_cd_codigo,
			[i18n.t('description') as string]: cubagem.eicb_ds_descricao,
			[i18n.t('type') as string]: cubagem.eicb_tx_tipo_modelo,
			[i18n.t('logging') as string]: cubagem.cubagem,
		})
	})
})

const kebabOptions = computed(() => [
	{
		value: 'editar',
		label: i18n.t('edit'),
	},
])

const headersAtivos = computed(() => {
	const ativos = tableHeaders.value.filter((f: any) => headersSelecionados.value.includes(f.key))
	ativos.forEach((f: any) => {
		f.isActive = true
	})
	return ativos
})

const ordensServicoFormatadas = computed(() => {
	return ordensServico.value.map((ordem: any) => ({
		Código: ordem.codigo,
		Descrição: ordem.descricao,
		Bitterlich: ordem.isBitterlich ? i18n.t('yes') : i18n.t('no'),
		Mapeado: ordem.isMapped ? i18n.t('yes') : i18n.t('no'),
		id: ordem.ordemServicoId,
	}))
})

const produtosFormatados = computed(() => {
	return produtos.value.map((produto: any) => ({
		Nome: produto.tppr_ds_descricao,
		Código: produto.tppr_cd_codigo,
		[i18n.t('logLength') as string]: produto.tppr_md_comprimento_tora,
		[i18n.t('maxDiameter') as string]: produto.tppr_md_diametro_max,
		[i18n.t('minDiameter') as string]: produto.tppr_md_diametro_min,
		id: produto.id,
	}))
})

const produtosEscolhidos = computed(() => {
	return produtos.value
		.filter((produto: any) => produtosSelecionados.value.includes(produto.id))
		.map((produto: any) => ({
			fk_eifl_estrato_invflo: model.value.id,
			fk_tppr_produto: produto.id,
			inpr_ds_descricao: produto.tppr_ds_descricao,
			inpr_is_com_casca: false,
			inpr_md_comprimento_max: produto.tppr_md_comprimento_tora,
			inpr_md_comprimento_min: produto.tppr_md_comprimento_tora,
			inpr_md_diametro_max: produto.tppr_md_diametro_max,
			inpr_md_diametro_min: produto.tppr_md_diametro_min,
		}))
})

const processingVariablesFormatted = computed(() => {
	const labelMap: Record<string, string> = {
		[ProcessingVariableEnum.VOLUME_TOTAL_COM_CASCA]: i18n.t('totalVolumeWithBark') as string,
		[ProcessingVariableEnum.VOLUME_TOTAL_SEM_CASCA]: i18n.t('totalVolumeWithOutBark') as string,
		[ProcessingVariableEnum.VOLUME_INDIVIDUAL_COM_CASCA]: i18n.t(
			'volumeIndividualComCasca',
		) as string,
		[ProcessingVariableEnum.VOLUME_INDIVIDUAL_SEM_CASCA]: i18n.t(
			'volumeIndividualSemCasca',
		) as string,
		[ProcessingVariableEnum.INCREMENTO_MEDIO_ANUAL_COM_CASCA]: i18n.t(
			'incrementoMedioAnualComCasca',
		) as string,
		[ProcessingVariableEnum.INCREMENTO_MEDIO_ANUAL_SEM_CASCA]: i18n.t(
			'incrementoMedioAnualSemCasca',
		) as string,
		[ProcessingVariableEnum.AREA_BASAL]: i18n.t('basalArea') as string,
		[ProcessingVariableEnum.DAP_MAXIMO]: i18n.t('maximumHBD') as string,
		[ProcessingVariableEnum.DAP_MEDIO]: i18n.t('averageHBD') as string,
		[ProcessingVariableEnum.DAP_MINIMO]: i18n.t('minimumHBD') as string,
		[ProcessingVariableEnum.DIAMETRO_MEDIO_QUADRATICO]: i18n.t('diametroMedioQuadratico') as string,
		[ProcessingVariableEnum.ALTURA_DOMINANTE]: i18n.t('dominantHeight') as string,
		[ProcessingVariableEnum.ALTURA_TOTAL_MAXIMA]: i18n.t('alturaTotalMaxima') as string,
		[ProcessingVariableEnum.ALTURA_TOTAL_MEDIA]: i18n.t('alturaTotalMedia') as string,
		[ProcessingVariableEnum.ALTURA_TOTAL_MINIMA]: i18n.t('alturaTotalMinima') as string,
		[ProcessingVariableEnum.NUMERO_DE_FUSTES]: i18n.t('stemNumber') as string,
		[ProcessingVariableEnum.NUMERO_DE_FUSTES_UTEIS]: i18n.t('numeroDeFustesUteis') as string,
	}
	return Object.values(ProcessingVariableEnum).map((v) => ({
		[i18n.t('name') as string]: labelMap[v],
		id: v,
	}))
})

const smartProcessingVariablesCheckboxValue = computed(() =>
	processingVariablesFormatted.value.map((opt) => ({
		key: opt.id,
		value: opt[i18n.t('name') as string],
		isActive: smartProcessingVariables.value.includes(opt.id),
	})),
)

const resolutionOptions = computed(() => [
	{ label: i18n.t('lowResolution') as string, value: ProcessingResolutionEnum.LOW },
	{ label: i18n.t('mediumResolution') as string, value: ProcessingResolutionEnum.MEDIUM },
	{ label: i18n.t('highResolution') as string, value: ProcessingResolutionEnum.HIGH },
])

const variaveisAgregadoras = computed(() => {
	return variaveis.value.toString().replaceAll(',', ' ').trim()
})

const isProcessamentoCubagemAfilamento = computed(() => {
	if (model.value.fk_eicb_estrato_invcbg) {
		const found = inventarioCubagens.value.find(
			(inventarioCubagem: any) => inventarioCubagem.id === model.value.fk_eicb_estrato_invcbg,
		)
		return found?.eicb_tx_tipo_modelo === 'Afilamento'
	}
	return false
})

const camposObrigatoriosPreenchidos = computed(() => {
	const descricaoPreenchida = model.value.eifl_ds_descricao
	const estratoPreenchido = model.value.fk_eicb_estrato_invcbg
	const medicaoOuOrdemPreenchido = model.value.fk_mdco_medicao || model.value.fk_orsv_ordem_servico
	const variaveisAgregadorasPreenchidas = variaveisAgregadoras.value
	const descricaoEditada = isEdit.value ? model.value.eifl_ds_descricao !== descricao.value : true
	const produtoPreenchidoSeAfilamento = isProcessamentoCubagemAfilamento.value
		? produtosSelecionados.value.length > 0
		: true
	const smartFieldsValid = isSmart.value ? smartProcessingVariables.value.length > 0 : true
	// A metodologia de numero de fustes so existe em singletrees, e la e obrigatoria: sem
	// esta validacao o payload saia sem a chave e o backend gravava o default do enum
	// silenciosamente (ver metodologiaNumeroFustesParaEnvio).
	// Nao usar !! aqui: Mapeamento e o ordinal 0 do enum, e !!0 e false -- um registro com
	// essa metodologia seria tratado como campo vazio. O sanitizador devolve undefined
	// exatamente nos casos invalidos ('' ou valor fora do enum) e deixa o 0 passar.
	const metodologiaPreenchidaSeSingleTrees = isSingleTrees.value
		? metodologiaNumeroFustesParaEnvio() !== undefined
		: true
	// A altura do toco so e editavel quando o processamento e de Afilamento; fora disso ela
	// fica no default numerico e nao ha o que validar. "0,3" e aceito (ver
	// alturaTocoParaEnvio), texto que nao vira numero e barrado antes de chegar na API.
	const alturaToco = alturaTocoParaEnvio()
	const alturaTocoValidaSeAfilamento = isProcessamentoCubagemAfilamento.value
		? typeof alturaToco === 'number' && alturaToco >= 0
		: true

	return (
		descricaoPreenchida &&
		estratoPreenchido &&
		medicaoOuOrdemPreenchido &&
		variaveisAgregadorasPreenchidas &&
		descricaoEditada &&
		produtoPreenchidoSeAfilamento &&
		smartFieldsValid &&
		metodologiaPreenchidaSeSingleTrees &&
		alturaTocoValidaSeAfilamento
	)
})

// ── Functions ──

/**
 * Membros do enum MetodologiaNumeroFustes do backend (Application.DAC.Constants.
 * ProcessamentoEnums). A ORDEM importa: o indice de cada item e o ordinal do enum em C#,
 * e e usada para converter ordinal -> nome em comMetodologiaNumeroFustesNormalizada.
 *
 * Serve tambem como guarda contra valores que nao sao membros validos -- '' vindo de um
 * cleanModel, ou rotulos de exibicao como 'Bitterlich' / 'Teorico' / 'Desconhecido'.
 * A grid nao produz mais esses rotulos (a sobrescrita foi removida de
 * formataEstratoInventarioParaTabela), mas a guarda continua barrando o caso do ''.
 */
const METODOLOGIAS_NUMERO_FUSTES_VALIDAS = [
	'Mapeamento',
	'AreaBasalBitterlich',
	'MortalidadeRoleta',
	'EspacamentoTeorico',
	'CadastroManual',
]

/**
 * Devolve uma COPIA do registro com a metodologia como nome de membro em vez de ordinal.
 *
 * O backend serializa o enum como numero, e o Dropdown casa a selecao por option.value,
 * que e string -- o watch dele ignora valores nao-string. Sem esta conversao, editar um
 * registro singletrees exibe o dropdown vazio mesmo com o registro tendo metodologia.
 *
 * A conversao e sem perda: o bind do backend aceita tanto o nome quanto o ordinal. Se o
 * backend passar a serializar como nome, isto vira no-op.
 */
function comMetodologiaNumeroFustesNormalizada(registro: any) {
	const valor = registro?.eifl_nm_metodologia_nfustes

	if (typeof valor !== 'number') return { ...registro }

	return {
		...registro,
		eifl_nm_metodologia_nfustes: METODOLOGIAS_NUMERO_FUSTES_VALIDAS[valor] ?? valor,
	}
}

/**
 * O InputText da altura do toco e type="text" sem coercao, entao o campo chega ao model
 * como string. Em pt-BR o usuario digita "0,3", que o parse decimal do backend nao
 * entende. Normaliza a virgula para ponto e devolve numero.
 *
 * Se o valor nao for numerico, devolve o valor cru: o comportamento passa a ser o mesmo
 * de antes desta correcao, em vez de inventar um default silencioso. Esse caso e barrado
 * por camposObrigatoriosPreenchidos nos botoes de criacao.
 */
function alturaTocoParaEnvio(): number | string {
	const valor = model.value.eifl_md_altura_toco

	if (typeof valor === 'number') return valor

	const normalizado = Number(
		String(valor ?? '')
			.trim()
			.replace(',', '.'),
	)

	return Number.isFinite(normalizado) ? normalizado : valor
}

/**
 * A metodologia de numero de fustes so e usada em processamento singletrees; no
 * tradicional a chave e sempre OMITIDA do payload (undefined desaparece no
 * JSON.stringify), que era justamente o caminho que funcionava.
 *
 * Nunca enviar '': o enum MetodologiaNumeroFustes nao converte string vazia e devolve
 * 400 ("Error converting value \"\" to type ... MetodologiaNumeroFustes"). Esse era o
 * estado que sobrava depois de um cleanModel ou de editar um registro.
 *
 * Numeros passam direto, pois o backend tambem aceita o ordinal do enum.
 */
function metodologiaNumeroFustesParaEnvio(): string | number | undefined {
	if (!isSingleTrees.value) return undefined

	const valor = model.value.eifl_nm_metodologia_nfustes

	if (typeof valor === 'number') return valor

	return METODOLOGIAS_NUMERO_FUSTES_VALIDAS.includes(valor) ? valor : undefined
}

function toggleExclusionAlert() {
	showExclusionAlert.value = !showExclusionAlert.value
}

function handleCheckboxChange(event: any) {
	filtrarMedicaoBruta.value = event.target.checked
}

function onDescriptionChange(value: string) {
	if (isEdit.value) {
		descriptionChanged.value = descricao.value !== value
	}
}

function handleVariaveis(variavel: string) {
	if (variaveis.value.includes(variavel)) {
		variaveis.value.splice(variaveis.value.indexOf(variavel), 1)
	} else {
		variaveis.value.push(variavel)
	}

	const conjunto = ['mtge_cd_codigo', 'ptio_dt_plantio']
	variaveis.value = variaveis.value.filter((f) =>
		conjunto.includes(variavel) ? conjunto.includes(f) : f === variavel,
	)
}

function sortConfigDate(event: any) {
	if (event.sortType === 'asc') {
		modelList.value.sort((a: any, b: any) => {
			const dateA = new Date(a.eifl_dt_processamento.split('/').reverse().join('-'))
			const dateB = new Date(b.eifl_dt_processamento.split('/').reverse().join('-'))
			return dateA.getTime() - dateB.getTime()
		})
	} else if (event.sortType === 'desc') {
		modelList.value.sort((a: any, b: any) => {
			const dateA = new Date(a.eifl_dt_processamento.split('/').reverse().join('-'))
			const dateB = new Date(b.eifl_dt_processamento.split('/').reverse().join('-'))
			return dateB.getTime() - dateA.getTime()
		})
	}
}

/**
 * Recarrega a listagem preservando pagina e filtro atuais. O GridPagination mantem o numero
 * da pagina no proprio estado e sempre pede blocos de 100 -- mesma convencao do evento
 * refreshTable que ele emite.
 */
function recarregarListagem() {
	const paginaAtual = (paginationRef.value as any)?.pageNumber

	if (paginaAtual) {
		refreshTabela(paginaAtual, 100, textFilter.value)
	} else {
		refreshTabela(0, 0, textFilter.value)
	}
}

/**
 * Marca ou desmarca um processamento como oficial. Chamada pelo icone do formulario e,
 * agora, pelo icone de cada linha da grid (coluna Status) -- ali o OfficialProcessingIcon
 * emitia o evento e ninguem escutava, entao o clique nao fazia nada.
 *
 * A estrela vira na hora para dar retorno imediato, mas o estado anterior e RESTAURADO se a
 * requisicao falhar. Antes o toggle era aplicado antes da chamada e nunca desfeito, o que
 * na grid deixaria a linha exibindo um estado que o backend nao aceitou.
 *
 * No sucesso a listagem e recarregada em vez de window.location.reload(): se "oficial" for
 * exclusivo, as outras linhas precisam refletir a mudanca, e recarregar a pagina inteira
 * aborta requisicoes em andamento (ver commit do processar-qualidade-tradicional).
 *
 * Sem id ainda (formulario de criacao) o toggle e apenas LOCAL: nao existe registro para
 * classificar no backend, e o valor viaja no proprio POST de criacao
 * (InventarioOficial em registraInventarioFlorestal). Antes a funcao retornava cedo nesse
 * caso, entao a estrela nascia acesa e o clique nao fazia nada -- era impossivel criar um
 * processamento nao oficial.
 */
function setarComoProcessamentoOficial(mdl: any) {
	if (!mdl) return

	if (!mdl.id || String(mdl.id).trim() === '') {
		mdl.inventarioOficial = !mdl.inventarioOficial
		return
	}

	if (classificandoOficial.value) return

	const estadoAnterior = mdl.inventarioOficial

	classificandoOficial.value = true
	mdl.inventarioOficial = !estadoAnterior

	const restauraEstado = (descricao?: string) => {
		mdl.inventarioOficial = estadoAnterior
		addMessage({
			title: i18n.t('aviso') as string,
			description: descricao ?? (i18n.t('erroAoAtualizarProcessamento') as string),
			type: 'warning',
		})
	}

	classificaComoInventarioOficial({
		EstratoInventarioFlorestalId: mdl.id,
		SingleTrees: mdl.eifl_is_singletrees,
		Oficial: mdl.inventarioOficial,
		NovoInventario: false,
		// O backend precisa da OS para resolver qual processamento singletrees classificar.
		// O caminho de criacao ja enviava (fk_orsv_ordem_servico em registraInventarioFlorestal);
		// aqui nao ia, entao a classificacao de um singletrees existente chegava sem a OS.
		// Vai como null em processamento tradicional, que nao tem OS vinculada.
		OrdemServicoId: mdl.fk_orsv_ordem_servico || null,
	})
		.then((response: any) => {
			// A API devolve o status tambem no corpo. Um statusCode diferente de 200 caia num
			// ramo silencioso: a estrela ficava virada sem nada ter sido gravado.
			if (response?.data?.statusCode && response.data.statusCode !== 200) {
				restauraEstado(response.data.message)
				return
			}

			addMessage({
				title: i18n.t('sucesso') as string,
				type: 'success',
			})
			recarregarListagem()
		})
		.catch((error: any) => {
			restauraEstado(error?.response?.data?.Message)
		})
		.finally(() => {
			classificandoOficial.value = false
		})
}

function deletaProcessamento(estratoInventarioFlorestalId: string | string[]) {
	const ids = Array.isArray(estratoInventarioFlorestalId)
		? estratoInventarioFlorestalId
		: [estratoInventarioFlorestalId]
	// Devolve a promise para quem precisa recarregar a grid DEPOIS do DELETE concluir --
	// um refreshTabela em paralelo pode reler o registro antes da exclusao e reexibi-lo.
	return deleteEstratoInventarioFlorestalByIdList(ids).then(() => {
		ids.forEach((id) => {
			const indexToRemove = modelList.value.map((m: any) => m.id).indexOf(id)
			if (indexToRemove !== -1) {
				modelList.value.splice(indexToRemove, 1)
			}
		})
	})
}

function verPlanejamento() {
	if (modelos.value) {
		planejamentoModelo.value = true
		showForm.value = false
	}
}

// Nao formatar eifl_nm_metodologia_nfustes aqui: a versao anterior sobrescrevia o campo
// do proprio registro com um rotulo de exibicao ('Bitterlich', 'Roleta', 'Teorico',
// 'Desconhecido'), que nao e membro do enum MetodologiaNumeroFustes. Como showEditModelForm
// faz model.value = { ...mdl }, o rotulo ia para o formulario -- o dropdown ficava vazio
// (o watch do Dropdown faz find e recebe undefined) e o PUT respondia 400 no bind.
// O rotulo nunca era exibido: NormalTable renderiza apenas as chaves de headers ativos via
// _.pick e tableHeaders nao tem esse campo, e o CSV usa lista fixa de colunas.
function formataEstratoInventarioParaTabela(ordemDeServico: any) {
	ordemDeServico.estrato =
		ordemDeServico.esfl_cd_codigo && ordemDeServico.esfl_ds_descricao
			? `${ordemDeServico.esfl_cd_codigo} - ${ordemDeServico.esfl_ds_descricao}`
			: ordemDeServico.esfl_ds_descricao || ''

	if (ordemDeServico.eifl_enum_tipo_processamento === ProcessingTypeEnum.SMART) {
		ordemDeServico.eifl_tx_status = `${ordemDeServico.eifl_tx_status} (Smart)`
	}

	ordemDeServico.isCheckedToDelete = false
}

async function registraInventarioFlorestal() {
	// Guarda de re-entrada: o Button nao desabilita com :loading (isDisabled olha apenas a
	// prop disabled), entao dois cliques rapidos disparavam dois POSTs de criacao.
	if (processando.value) return

	processando.value = true
	mostrarLoadingProcessando.value = true
	mensagemProcessando.value = 'Aguarde enquanto os modelos são gerados'
	const isSubsequente =
		produtosEscolhidos.value && produtosEscolhidos.value.length > 0
			? true
			: model.value.eifl_is_subsequente
	model.value.eifl_is_subsequente = isSubsequente

	try {
		let entityId: string

		if (isSmart.value) {
			const smartStore = useSmartProcessingStore()
			const repository = new ForestInventoryStratumRepository(registryApi)
			const entity = new ForestInventoryStratum(
				model.value.eifl_ds_descricao,
				new Date(),
				new Date(),
				isSubsequente,
				model.value.eifl_md_altura_toco,
				model.value.fk_mdco_medicao,
				model.value.eifl_md_nivel_significancia,
				model.value.inventarioOficial,
				true,
				ProcessingTypeEnum.SMART,
				smartProcessingVariables.value as ProcessingVariableEnum[],
				smartResolution.value as ProcessingResolutionEnum,
				smartStore.selectedImageIds,
				variaveisAgregadoras.value,
				produtosEscolhidos.value,
				model.value.fk_eicb_estrato_invcbg,
			)
			entityId = await repository.create(entity)
		} else {
			const response = await createEstratoInventario({
				eifl_ds_descricao: model.value.eifl_ds_descricao,
				eifl_dt_processamento: new Date(),
				eifl_dt_referencia: new Date(),
				eifl_is_subsequente: isSubsequente,
				eifl_md_altura_toco: alturaTocoParaEnvio(),
				eifl_tx_var_agregadoras: variaveisAgregadoras.value,
				fk_mdco_medicao: model.value.fk_mdco_medicao,
				fk_eicb_estrato_invcbg: model.value.fk_eicb_estrato_invcbg,
				eifl_cd_codigo: 0,
				eifl_md_volume_florestal: 0,
				eifl_tx_status: '',
				eifl_tx_query_gerada: '',
				InventarioProdutos: produtosEscolhidos.value,
				fk_orsv_ordem_servico: model.value.fk_orsv_ordem_servico,
				eifl_is_singletrees: isSingleTrees.value,
				eifl_md_nivel_significancia: model.value.eifl_md_nivel_significancia,
				eifl_nm_metodologia_nfustes: metodologiaNumeroFustesParaEnvio(),
				InventarioOficial: model.value.inventarioOficial,
				NovoInventario: true,
				// Mesmo campo que o toggle da estrela envia: o backend usa a OS para classificar o
				// processamento singletrees como oficial. fk_orsv_ordem_servico acima continua sendo
				// o vinculo gravado no proprio registro -- sao usos diferentes do mesmo id.
				OrdemServicoId: model.value.fk_orsv_ordem_servico || null,
			})
			entityId = response.data
		}

		model.value.id = entityId
		addMessage({
			title: i18n.t('aviso') as string,
			description: i18n.t('ajustandoModelosHipsometricos') as string,
			type: 'info',
		})

		const hipsResponse = await ajustaModeloHipsometrico({
			EiflId: model.value.id,
			MedicaoId: model.value.fk_mdco_medicao || model.value.fk_orsv_ordem_servico,
			VariavelAgregadora: variaveisAgregadoras.value.replace(' ', '-'),
			isSingleTrees: isSingleTrees.value,
		})

		modelos.value = hipsResponse.data.result
		processando.value = false
		mostrarLoadingProcessando.value = false
	} catch {
		planejamentoModelo.value = true
		// Sem liberar as flags, uma falha na criacao deixaria o botao "Processar" morto
		// ate recarregar a pagina.
		processando.value = false
		mostrarLoadingProcessando.value = false
		addMessage({
			title: i18n.t('erro') as string,
			description: i18n.t('falhaAoCriarProcessamento') as string,
			type: 'error',
		})
		// Quando a propria criacao falha o id ainda e '', e sem esta condicao era
		// disparado um DELETE com [''] para a API.
		if (model.value.id) {
			deletaProcessamento([model.value.id])
		}
	}
}

/**
 * O walter responde HTTP 200 carregando o status real no corpo (ver comentario em
 * classificaComoInventarioOficial). Trata qualquer 2xx como sucesso -- um job assincrono
 * pode responder 202 Accepted legitimamente, e barrar isso seria um falso positivo.
 * Corpo sem statusCode continua valendo como sucesso: nao da para inferir falha da ausencia
 * do campo, senao endpoints que nao seguem esse padrao quebrariam.
 */
function respostaWalterFalhou(response: any): boolean {
	const statusCode = response?.data?.statusCode
	if (typeof statusCode !== 'number') return false
	return statusCode < 200 || statusCode >= 300
}

function saveModel() {
	escolhendoModelo.value = true
	const userModules = JSON.parse(localStorage.getItem('user-modules') || '[]')

	if (
		!userModules.includes('IA_SELECTED_MODELS') &&
		modelos.value[index.value].stan_nm_variavel_modelo === 'Ajuste11'
	) {
		escolhendoModelo.value = false
		addMessage({
			title: i18n.t('aviso') as string,
			description: i18n.t('avisoSemModuloIASelectedModel') as string,
			type: 'warning',
		})
		return
	}

	// Volta para a listagem e atualiza a grid sem recarregar a pagina.
	// No caminho de sucesso so deve rodar DEPOIS que a qualidade terminar: um
	// window.location.reload() aqui abortaria a request de qualidade ainda em andamento.
	const finalizar = () => {
		escolhendoModelo.value = false
		planejamentoModelo.value = false
		showForm.value = false
		isEdit.value = false
		index.value = 0
		modelos.value = []
		modeloSalvo.value = undefined
		cleanModel(model)
		produtosSelecionados.value = []
		refreshTabela(0, 0, '')
	}

	// Convencao deste fluxo: processamento que falha nao deixa registro para tras
	// (ver o catch de registraInventarioFlorestal e a tela de InventarioQualidade).
	// Sem isto sobrava na grid um registro com status 'Hipsometria' e sem resultados.
	// A condicao no id repete a guarda de registraInventarioFlorestal: sem ela era
	// disparado um DELETE com [''] para a API.
	const descartarProcessamentoFalho = () => {
		const id = model.value.id
		if (!id) {
			finalizar()
			return
		}
		// finalizar() so depois do DELETE, senao o refreshTabela pode reler o registro
		// antes da exclusao concluir. Falha no DELETE nao impede voltar para a listagem:
		// o registro fica, mas o formulario nao pode travar por causa disso.
		deletaProcessamento([id])
			.catch((erro: any) =>
				console.error('[inventario] falha ao excluir processamento incompleto:', erro),
			)
			.finally(finalizar)
	}

	addMessage({
		title: i18n.t('aviso') as string,
		description: i18n.t('salvandoModelo') as string,
		type: 'info',
	})
	saveChosenHipsometricoModel({
		EiflId: model.value.id,
		MedicaoId: model.value.fk_mdco_medicao || model.value.fk_orsv_ordem_servico,
		ChosenModelName: modelos.value[index.value].stan_nm_variavel_modelo,
		isSingleTrees: isSingleTrees.value,
	})
		.then((response: any) => {
			// O walter responde HTTP 200 com o erro no corpo (mesmo padrao tratado em
			// classificaComoInventarioOficial). Sem esta guarda o .then seguia como sucesso.
			if (respostaWalterFalhou(response)) {
				console.error('[inventario] SaveChosenHipsometricoModel falhou:', response?.data)
				addMessage({
					title: i18n.t('erro') as string,
					description: response.data.message || (i18n.t('erroAoSalvarModelo') as string),
					type: 'error',
				})
				escolhendoModelo.value = false
				return
			}

			addMessage({
				title: i18n.t('aviso') as string,
				description: i18n.t('iniciandoProcessamento') as string,
				type: 'info',
			})
			processaInventario({
				EiflId: model.value.id,
				EicbId: model.value.fk_eicb_estrato_invcbg,
				DiametroComercial: null,
				AlturaToco: alturaTocoParaEnvio(),
				IsSubsequente: model.value.eifl_is_subsequente,
				Processamento: isProcessamentoCubagemAfilamento.value ? 'Sortimento' : 'Volumetrico',
				isSingleTrees: isSingleTrees.value,
				ordemServicoId: model.value.fk_orsv_ordem_servico || null,
				nivelSignificancia: model.value.eifl_md_nivel_significancia,
				metodologiaNFustes: metodologiaNumeroFustesParaEnvio(),
			})
				.then((response: any) => {
					// Mesma guarda do passo anterior: o walter pode responder HTTP 200 com o erro
					// no corpo -- ja visto na pratica com statusCode 500 e a excecao do script R
					// em responseException.Message. Sem isto o fluxo chamava a qualidade sobre um
					// inventario que nao processou, e o 400 dela aparecia como se fosse a causa.
					// O corpo vai para o console porque a message de topo costuma vir null e o
					// erro real fica em responseException.
					if (respostaWalterFalhou(response)) {
						console.error('[inventario] ProcessaInventario falhou:', response?.data)
						addMessage({
							title: i18n.t('erro') as string,
							description:
								response.data.message || (i18n.t('erroAoSalvarProcessamento') as string),
							type: 'error',
						})
						descartarProcessamentoFalho()
						return
					}

					addMessage({
						title: i18n.t('aviso') as string,
						description: 'Processando informações de qualidade para o inventário',
						type: 'info',
					})

					if (!isSingleTrees.value && !isSmart.value) {
						processarQualidadeParaInventarioTradicional({
							idInventarioFlorestal: model.value.id,
							idMedicao: model.value.fk_mdco_medicao,
							nivelSignificancia: model.value.eifl_md_nivel_significancia,
						})
							.then(() => {
								escolhendoModelo.value = false
								planejamentoModelo.value = false
								showForm.value = false
								addMessage({
									title: i18n.t('sucesso') as string,
									description: i18n.t('planejamentoSalvoComSucesso') as string,
									type: 'success',
								})
								// So recarrega apos a qualidade concluir, evitando cancelar a request
								finalizar()
							})
							.catch((error: any) => {
								console.error(
									'Erro ao processar qualidade para inventario tradicional:',
									error?.response?.data ?? error?.message ?? error,
								)
								// A qualidade e um passo best-effort: por requisito, falhar aqui NAO
								// invalida o processamento tradicional, que deve seguir e gerar os
								// resultados. Por isso e um aviso e nao um erro, e o fluxo finaliza
								// normalmente.
								// ATENCAO: o 200 do ProcessaInventario significa apenas que o job foi
								// aceito, nao que o inventario terminou -- o processamento roda em
								// segundo plano (~20min) e a conclusao chega pela notificacao
								// 'Processamento em segundo plano' via SignalR, que esta tela escuta
								// em onProcessamentoConcluido. Por isso a mensagem fala em "enviado
								// para processamento" e nao em "processado".
								// finalizar() tambem evita deixar o formulario aberto com
								// model.value.id preenchido -- um novo clique em "Salvar modelo"
								// reprocessava o MESMO id.
								addMessage({
									title: i18n.t('aviso') as string,
									description: i18n.t('inventarioSalvoQualidadeFalhou') as string,
									type: 'warning',
								})
								finalizar()
							})
					} else {
						finalizar()
					}
				})
				.catch((error: any) => {
					console.error(
						'Erro ao salvar processamento:',
						error?.response?.data ?? error?.message ?? error,
					)
					addMessage({
						title: i18n.t('erro') as string,
						description: i18n.t('erroAoSalvarProcessamento') as string,
						type: 'error',
					})
					// Mesmo descarte do erro devolvido em corpo 200: falhou o processamento,
					// nao deixa registro incompleto na grid.
					descartarProcessamentoFalho()
				})
		})
		.catch((error: any) => {
			console.log('Erro ao salvar modelo:', error?.response?.data ?? error?.message ?? error)
			addMessage({
				title: i18n.t('erro') as string,
				description: i18n.t('erroAoSalvarModelo') as string,
				type: 'error',
			})
		})
}

function searchModel(text: string) {
	const modelo = modelos.value.find((m: any) =>
		m.stan_nm_titulo_modelo.toLowerCase().includes(text.toLowerCase()),
	)
	if (modelo) {
		index.value = modelos.value.indexOf(modelo)
	}
}

function handleClick(tipo: string) {
	avancado.value = tipo === 'avancado'
}

async function update() {
	try {
		if (isSmart.value) {
			const repository = new ForestInventoryStratumRepository(registryApi)
			const entity = new ForestInventoryStratum(
				model.value.eifl_ds_descricao,
				new Date(model.value.eifl_dt_processamento.split('/').reverse().join('-')),
				new Date(),
				model.value.eifl_is_subsequente,
				model.value.eifl_md_altura_toco,
				model.value.fk_mdco_medicao,
				model.value.eifl_md_nivel_significancia,
				model.value.inventarioOficial,
				false,
				ProcessingTypeEnum.SMART,
				smartProcessingVariables.value as ProcessingVariableEnum[],
				smartResolution.value as ProcessingResolutionEnum,
				[],
				variaveisAgregadoras.value,
				[],
				model.value.fk_eicb_estrato_invcbg,
				model.value.id,
				model.value.eifl_tx_status || '',
				model.value.eicb_cd_codigo || 0,
				model.value.eifl_md_diametro_comercial,
			)
			await repository.update(entity)
		} else {
			await updateEstratoInventarioFlorestalAsync({
				id: model.value.id,
				eifl_ds_descricao: model.value.eifl_ds_descricao,
				eifl_tx_var_agregadoras: variaveisAgregadoras.value,
				eifl_tx_status: model.value.eifl_tx_status,
				eicb_cd_codigo: model.value.eicb_cd_codigo,
				eifl_is_singletrees: isSingleTrees.value,
				eifl_is_subsequente: model.value.eifl_is_subsequente,
				eifl_md_altura_toco: alturaTocoParaEnvio(),
				eifl_md_diametro_comercial: model.value.eifl_md_diametro_comercial,
				eifl_md_nivel_significancia: model.value.eifl_md_nivel_significancia,
				fk_eicb_estrato_invcbg: model.value.fk_eicb_estrato_invcbg,
				fk_mdco_medicao: model.value.fk_mdco_medicao,
				fk_orsv_ordem_servico: model.value.fk_orsv_ordem_servico,
				// NAO usar metodologiaNumeroFustesParaEnvio() aqui: neste PUT a grid pode ter
				// deixado no model um rotulo de exibicao ('Bitterlich', 'Roleta', 'Teorico'),
				// e omitir a chave faria o backend regravar a metodologia com o default do enum
				// silenciosamente. Melhor falhar alto com 400 ate a formatacao da grid ser
				// corrigida (ver formataEstratoInventarioParaTabela).
				eifl_nm_metodologia_nfustes: model.value.eifl_nm_metodologia_nfustes,
			})
		}

		addMessage({ title: i18n.t('sucesso') as string, type: 'success' })
		showForm.value = false
		descriptionChanged.value = false
		window.location.reload()
	} catch {
		addMessage({
			title: i18n.t('erro') as string,
			description: i18n.t('erroAoAtualizarProcessamento') as string,
			type: 'error',
		})
	}
}

function deleteModels(email: string) {
	const emailUsuario = localStorage.getItem('email')
	if (email === emailUsuario) {
		const inventarioSelecionado = modelList.value.filter((inventarioFlorestal: any) =>
			modelsIdWithCheckedToDelete.value.includes(inventarioFlorestal.id),
		)

		const inventariosSelecionados = inventarioSelecionado.map(
			(inventarioFlorestal: any) =>
				`• ${inventarioFlorestal.eifl_cd_codigo} / ${inventarioFlorestal.eifl_ds_descricao}\n`,
		)

		const confirmResult = confirm(i18n.t('confirmDelete') + '\n ' + inventariosSelecionados)

		if (confirmResult) {
			deleteEstratoInventarioFlorestalByIdList(modelsIdWithCheckedToDelete.value).then(() => {
				modelsIdWithCheckedToDelete.value.forEach((f: string) => {
					const indexToRemove = modelList.value.map((m: any) => m.id).indexOf(f)
					modelList.value.splice(indexToRemove, 1)
				})
				addMessage({
					title: i18n.t('sucesso') as string,
					type: 'success',
				})
				showExclusionAlert.value = false
			})
		}
	} else {
		addMessage({
			title: i18n.t('wrongEmail') as string,
			description: i18n.t('wrongEmaildescricao') as string,
			type: 'error',
		})
	}
}

function saveClick() {
	if (isEdit.value) {
		update()
	} else if (isSmart.value) {
		// O radio desabilitado nao basta: tipoProcessamento tambem e setado por codigo no
		// retorno de selectGeoImages, e o modulo pode ter sido removido no meio do fluxo.
		if (!possuiModuloProcessamentoSmart.value) {
			addMessage({
				title: i18n.t('aviso') as string,
				description: i18n.t('avisoSemAcessoAoModulo') as string,
				type: 'warning',
			})
			return
		}

		const selectedMeasurement = medicoesOriginal.value.find(
			(m: any) => m.id === model.value.fk_mdco_medicao,
		)
		const matchingEstrato = estratosFlorestais.value.find(
			(e: any) => e.esfl_cd_codigo === selectedMeasurement?.codigoEstratoFlorestal,
		)
		const smartStore = useSmartProcessingStore()
		smartStore.populateFromForm({
			measurementId: model.value.fk_mdco_medicao,
			farmId: matchingEstrato?.fk_fzda_fazenda ?? '',
			description: model.value.eifl_ds_descricao,
			significanceLevel: model.value.eifl_md_nivel_significancia,
			stumpHeight: model.value.eifl_md_altura_toco,
			officialInventory: model.value.inventarioOficial,
			processingVariables: smartProcessingVariables.value as ProcessingVariableEnum[],
			resolution: smartResolution.value as ProcessingResolutionEnum,
			volumeInventoryStratumId: model.value.fk_eicb_estrato_invcbg,
			aggregatingVariables: variaveisAgregadoras.value,
			selectedProductIds: produtosSelecionados.value,
		})
		showForm.value = false
		router.push({ name: 'selectGeoImages' })
	} else {
		planejamentoModelo.value = true
		registraInventarioFlorestal()
		showForm.value = false
	}
}

function showEditModelForm(mdl: any) {
	produtosSelecionados.value = mdl.produtos?.map((m: any) => m.fk_tppr_produto) || []
	model.value = comMetodologiaNumeroFustesNormalizada(mdl)
	descricao.value = model.value.eifl_ds_descricao
	nivelEstrato.value = agregadoras.value[model.value.eifl_tx_var_agregadoras] ?? ''
	variaveis.value = (model.value.eifl_tx_var_agregadoras ?? '').split(' ')
	titleForm.value = `${i18n.t('edit')} ${i18n.t('inventoryProcessing')}`
	isEdit.value = true

	if (mdl.eifl_enum_tipo_processamento === ProcessingTypeEnum.SMART) {
		tipoProcessamento.value = ProcessingTypeEnum.SMART
		smartProcessingVariables.value = mdl.eifl_list_variaveis_processamento || []
		smartResolution.value = mdl.eifl_enum_resolucao_processamento || ProcessingResolutionEnum.MEDIUM
	} else if (mdl.eifl_is_singletrees) {
		tipoProcessamento.value = ProcessingTypeEnum.SINGLE_TREES
	} else {
		tipoProcessamento.value = ProcessingTypeEnum.TRADITIONAL
	}

	changeFormAndCloseAllActions()

	getAllHipsometricoWithGraphicByEiflId(model.value.id).then((response: any) => {
		isEdit.value = true
		model.value = comMetodologiaNumeroFustesNormalizada(mdl)
		modelos.value = response.data.result
		modeloSalvo.value = response.data.result
	})
}

function showNewModelForm() {
	cleanModel(model)
	executoresSelecionados.value = []
	variaveis.value = []
	// Sem esse reset os produtos de um registro editado antes vazam para o novo
	// processamento e ainda forcam eifl_is_subsequente = true em registraInventarioFlorestal.
	produtosSelecionados.value = []
	tipoProcessamento.value = ProcessingTypeEnum.TRADITIONAL
	model.value.eifl_md_altura_toco = 0.1
	model.value.eifl_md_nivel_significancia = 0.05

	// Reset smart fields
	smartProcessingVariables.value = []
	smartResolution.value = ProcessingResolutionEnum.MEDIUM

	titleForm.value = `${i18n.t('create')} ${i18n.t('inventoryProcessing')}`
	isEdit.value = false
	model.value.inventarioOficial = true
	changeFormAndCloseAllActions()
}

function changeFormAndCloseAllActions() {
	showForm.value = !showForm.value
	modelos.value = []
	descriptionChanged.value = false
}

/**
 * Baixa CSV com o resultado FILTRADO COMPLETO (nao so a pagina ja carregada):
 * busca tudo numa chamada antes de gerar o arquivo, sem mexer no estado da grid.
 */
async function downloadCSVProcessamentoInventarioFlorestal() {
	if (exportando.value) return
	exportando.value = true
	try {
		const response: any = await getAllEstratosInventarioFlorestalPagination(
			0,
			Math.max(totalRecords.value, 100),
			textFilter.value,
		)
		const items = response.data.result || []

		// Mesma formatacao aplicada pela grid ao carregar (refreshTabela)
		items.forEach((item: any) => {
			const dataProcessamento = new Date(item.eifl_dt_processamento)
			const dia = String(dataProcessamento.getDate()).padStart(2, '0')
			const mes = String(dataProcessamento.getMonth() + 1).padStart(2, '0')
			const ano = dataProcessamento.getFullYear()
			item.eifl_dt_processamento = `${dia}/${mes}/${ano}`
			formataEstratoInventarioParaTabela(item)
		})

		const cabecalhoDoCsvInventarioFlorestal = [
			'Código',
			'Data do Processamento',
			'Descrição',
			'Estrato Florestal',
			'Ordem de Serviço',
			'Medição',
			'Estado',
			'Modelo',
		]

		const linhas = items.map((item: any) => [
			item.eifl_cd_codigo,
			item.eifl_dt_processamento,
			item.eifl_ds_descricao,
			item.estrato,
			item.ordemDeServico,
			item.mdco_ds_descricao,
			item.eifl_tx_status,
			item.nomeModelo,
		])

		exportarCsv(
			'export_processamentos_inventario_florestal',
			cabecalhoDoCsvInventarioFlorestal,
			linhas,
		)
	} finally {
		exportando.value = false
	}
}

function refreshTabela(pageNumber?: number, pageSize?: number, filteringWord?: string) {
	getAllEstratosInventarioFlorestalPagination(
		pageNumber ?? 0,
		pageSize ?? 0,
		filteringWord ?? '',
	).then((response: any) => {
		const inventarios = response.data.result
		// A leitura de result[0] vinha ANTES desta guarda e estourava com resultado vazio --
		// alcancavel agora que a listagem tambem e recarregada pelo toggle de oficial.
		if (!inventarios || inventarios.length === 0) {
			modelList.value = []
			totalRecords.value = 0
			tableConfiguration.value.statusTable = i18n.t('noRecordFound') as string
			return
		}
		totalRecords.value = inventarios[0].totalRegistros
		inventarios.forEach((inventario: any) => {
			const dataProcessamento = new Date(inventario.eifl_dt_processamento)
			const dia = String(dataProcessamento.getDate()).padStart(2, '0')
			const mes = String(dataProcessamento.getMonth() + 1).padStart(2, '0')
			const ano = dataProcessamento.getFullYear()
			inventario.eifl_dt_processamento = `${dia}/${mes}/${ano}`
			formataEstratoInventarioParaTabela(inventario)
		})
		modelList.value = inventarios
		tableConfiguration.value.statusTable = i18n.t('noRecordFound') as string
	})
}

// ── Watchers ──
watch(
	tableHeaders,
	(value) => {
		headersSelecionados.value = value.filter((f: any) => f.isActive).map((m: any) => m.key)
	},
	{ deep: true },
)

watch(intention, (value) => {
	const foundModel = modelList.value.find((m: any) => m.id === value.properties?.id)
	if (!foundModel) return
	switch (value.intent) {
		case 'editar':
			showEditModelForm(foundModel)
			break
		case 'visualizar':
			getAllHipsometricoWithGraphicByEiflId(foundModel.id).then((response: any) => {
				isEdit.value = true
				// A normalizacao tambem devolve copia: antes o model virava um alias da linha
				// da grid, e qualquer edicao no formulario escrevia direto na listagem.
				model.value = comMetodologiaNumeroFustesNormalizada(foundModel)
				modelos.value = response.data.result
				planejamentoModelo.value = true
			})
			break
	}
})

watch(isSingleTrees, (value) => {
	if (value) nivelEstrato.value = ''
})

// Ao trocar a cubagem para um modelo que nao e de Afilamento o dropdown de produtos e
// destruido pelo v-if, mas produtosSelecionados continuava preenchido e ia no payload.
// So limpa na transicao true -> false: enquanto inventarioCubagens nao carregou o
// computed e falso-negativo, e nesse estado ele nunca chegou a ser true.
watch(isProcessamentoCubagemAfilamento, (isAfilamento, eraAfilamento) => {
	if (eraAfilamento && !isAfilamento) {
		produtosSelecionados.value = []
	}
})

watch(
	() => model.value.fk_tpsv_tipo_servico,
	() => {
		model.value.orsv_md_medida_parcela = ''
		model.value.orsv_tx_forma_parcela = ''
		model.value.orsv_md_geofence = ''
	},
)

watch(textFilter, (value) => {
	refreshTabela(0, 0, value)
})

// ── Provide ──
provide('edit', edit)

// ── Refresh ao concluir processamento em segundo plano ──
function onProcessamentoConcluido() {
	refreshTabela(0, quantityPerPage.value, textFilter.value)
}

onMounted(() => {
	window.addEventListener('processamento:concluido', onProcessamentoConcluido)
})

onUnmounted(() => {
	window.removeEventListener('processamento:concluido', onProcessamentoConcluido)
})

// ── Initialization (equivalent to created()) ──
quantityPerPage.value = 10
orderTable.value = ['orsv_cd_codigo']

const produtosPromise = getAllProdutos().then((response: any) => {
	produtos.value = response.data.result
})

getAllEstratoInventarioCubagem().then((response: any) => {
	inventarioCubagens.value = response.data.result
})

getAllEstratosInventarioFlorestalPagination(0, 0, '').then((response: any) => {
	const inventarios = response.data.result
	totalRecords.value = response.data.result[0].totalRegistros
	inventarios.forEach((inventario: any) => {
		const dataProcessamento = new Date(inventario.eifl_dt_processamento)
		const dia = String(dataProcessamento.getDate()).padStart(2, '0')
		const mes = String(dataProcessamento.getMonth() + 1).padStart(2, '0')
		const ano = dataProcessamento.getFullYear()
		inventario.eifl_dt_processamento = `${dia}/${mes}/${ano}`
		formataEstratoInventarioParaTabela(inventario)
	})
	modelList.value = inventarios
	tableConfiguration.value.statusTable = i18n.t('noRecordFound') as string
})

getDropdownEstratosFlorestais().then((response: any) => {
	estratosFlorestais.value = response
})

getDropdownMedicaoProcessamento().then((response: any) => {
	medicoesOriginal.value = response
})

getInstalacoesSingleTrees().then((response: any) => {
	ordensServico.value = response.data.result
})

// ── Detect return from SelectGeoImages (SMART flow) ──
const smartStore = useSmartProcessingStore()
if (smartStore.justReturnedFromImageSelection) {
	model.value.eifl_ds_descricao = smartStore.description
	model.value.fk_mdco_medicao = smartStore.measurementId
	model.value.eifl_md_altura_toco = smartStore.stumpHeight
	model.value.eifl_md_nivel_significancia = smartStore.significanceLevel
	model.value.inventarioOficial = smartStore.officialInventory
	model.value.fk_eicb_estrato_invcbg = smartStore.volumeInventoryStratumId
	smartProcessingVariables.value = smartStore.processingVariables as string[]
	smartResolution.value = smartStore.resolution
	variaveis.value = smartStore.aggregatingVariables.split(' ')
	produtosSelecionados.value = smartStore.selectedProductIds
	tipoProcessamento.value = ProcessingTypeEnum.SMART

	planejamentoModelo.value = true
	showForm.value = false
	produtosPromise.then(() => {
		registraInventarioFlorestal().finally(() => {
			smartStore.clearReturnFlag()
		})
	})
}

return { __sfc: true,addMessage, paginationRef, quantityPerPage, setStartEnd, setQuantityPerPage, disableSaveButton, isEdit, modelList, orderTable, showForm, titleForm, textFilter, sortBy, sortType, isDeleteButtonDisabled, modelsIdWithCheckedToDelete, modelsToBeShowedAndTotal, cleanModel, router, tipoProcessamento, medicoesOriginal, filtrarMedicaoBruta, variaveis, totalRecords, exportando, showExclusionAlert, descricao, modeloSalvo, escolhendoModelo, processando, classificandoOficial, descriptionChanged, headersSelecionados, ordensServico, modeloEscolhido, index, modelos, avancado, nivelEstrato, produtosSelecionados, inventarioCubagens, produtos, intention, planejamentoModelo, mostrarLoadingProcessando, mensagemProcessando, smartProcessingVariables, smartResolution, model, tableConfiguration, tableHeaders, estratosFlorestais, executoresSelecionados, medicoesComputadas, isSingleTrees, isSmart, hasSmartMeasurements, possuiModuloProcessamentoSmart, smartIndisponivel, opcaoSmart, agregadoras, edit, cubagens, kebabOptions, headersAtivos, ordensServicoFormatadas, produtosFormatados, produtosEscolhidos, processingVariablesFormatted, smartProcessingVariablesCheckboxValue, resolutionOptions, variaveisAgregadoras, isProcessamentoCubagemAfilamento, camposObrigatoriosPreenchidos, METODOLOGIAS_NUMERO_FUSTES_VALIDAS, comMetodologiaNumeroFustesNormalizada, alturaTocoParaEnvio, metodologiaNumeroFustesParaEnvio, toggleExclusionAlert, handleCheckboxChange, onDescriptionChange, handleVariaveis, sortConfigDate, recarregarListagem, setarComoProcessamentoOficial, deletaProcessamento, verPlanejamento, formataEstratoInventarioParaTabela, registraInventarioFlorestal, respostaWalterFalhou, saveModel, searchModel, handleClick, update, deleteModels, saveClick, showEditModelForm, showNewModelForm, changeFormAndCloseAllActions, downloadCSVProcessamentoInventarioFlorestal, refreshTabela, onProcessamentoConcluido, produtosPromise, smartStore, Button, CrudForm, CrudTable, Dropdown, GridPagination, RadioButtons, Tag, TagWithButton, CardPicker, DropdownCheckbox, DropdownCheckboxFk, DropdownFk, InputSearch, InputText, KebabMenu, Loading, ModelPreviewTemp, ProcessamentoAvancado, OfficialProcessingIcon, ExclusionAlert, DropdownCheckboxFKMedicao }
}

})